'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft, CheckCircle2, ClipboardList, Clock, FileSearch, FileSignature, FilePlus2, FileText,
  Briefcase, CalendarDays, Eye, FolderOpen, Gavel, History, Landmark, Microscope, PauseCircle, Scale, Send, ShieldAlert, Sparkles, Stamp, UserRound, Users,
  AlertTriangle, Printer, type LucideIcon,
} from 'lucide-react';
import { imprimirElementoComoPDF } from '@/lib/imprimirPantalla';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import {
  getExpediente, fijarFechaHechos,
  type ExpedienteDetalle as ExpDetalle, type Hechos, type ActaRepartoResumen,
} from '@/lib/expedientes';
import {
  obtenerSolicitud, listarFirmantes, type Solicitud, type Firmante,
} from '@/lib/preRadicacion';
import { labelDecision } from '@/lib/preRadicacion';
import { descargarDocumento } from '@/lib/documentos';
import ActaRepartoDialog from '@/components/ActaRepartoDialog';
import {
  getActuaciones, getTerminos, getPruebas, getNulidades,
  crearActuacion, enviarRevision, revisarActuacion, firmarActuacion,
  labelEstadoTermino, labelTermino,
  autoInhibitorio, indagacionPrevia, iniciarInvestigacion,
  patchIndagacion, archivarIndagacion, abrirInvestigacionDesdeIndagacion,
  prorrogaInvestigacion, versionLibre, notificacionInvestigacion,
  cerrarInvestigacion, alegatos, evaluacionArchivo, crearPliego, notificarPliego,
  remitirJuzgamiento, usuarioActual, puedeFirmar, puedeRevisar,
  type Actuacion, type Termino, type Prueba, type Nulidad, type Semaforo,
} from '@/lib/instruccion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import HeroVista from '@/components/HeroVista';
import AgendaExpediente from '@/components/AgendaExpediente';
import LineaTiempoProceso from '@/components/LineaTiempoProceso';
import VisorDocumento from '@/components/VisorDocumento';
import TablaTerminos, { type DestinoSeccion } from '@/components/TablaTerminos';
import TarjetaSeccion, { BOTON_CABECERA, DatoTarjeta, INSIGNIA_CABECERA } from '@/components/TarjetaSeccion';
import HistorialDefensores from '@/components/HistorialDefensores';
import { ExpedienteActualContext } from '@/lib/expedienteActual';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { caminoDeterminado, labelSituacion } from '@/lib/decisionFlujo';
import PanelDecisionInicial from './PanelDecisionInicial';
import PanelJuzgamiento from './PanelJuzgamiento';
import PanelSegundaInstancia from './PanelSegundaInstancia';
import PanelSanciones from './PanelSanciones';
import PanelDocumentos from './PanelDocumentos';
import PanelPruebas from './PanelPruebas';
import PanelNulidades from './PanelNulidades';
import PanelSuspension from './PanelSuspension';
import PanelRecursos from './PanelRecursos';
import PanelOtrosDocumentos from './PanelOtrosDocumentos';
import { DialogoCorreccion, PanelCorrecciones, type PedidoCorreccion } from '@/components/CorreccionesErrores';
import AutoDecisionFlujo from '@/components/decisiones/AutoDecisionFlujo';
import { DOC_FLUJO_FORMULACION } from '@/lib/plantillas/formulacion';
import { DOC_FLUJO_CIERRE } from '@/lib/plantillas/cierre';
import type { DocFlujoConfig } from '@/lib/plantillas/documentos-flujo';
import CampoPDF from '@/components/CampoPDF';
import AvanceProceso from '@/components/AvanceProceso';

// ¿El expediente está en fase de juzgamiento? (coincidencia laxa por palabra clave).
function enJuzgamiento(estado?: string): boolean {
  const e = (estado || '').toUpperCase();
  return [
    'PENDIENTE_REPARTO_JUZGAMIENTO', 'REPARTO_JUZGAMIENTO', 'JUICIO_ORDINARIO',
    'JUICIO_VERBAL', 'VARIACION_CARGOS', 'ALEGATOS_CONCLUSION',
    'FALLO_PRIMERA_INSTANCIA', 'RECURSO_INTERPUESTO',
  ].some((k) => e.includes(k)) || /JUZGAM|JUICIO|VARIACION|ALEGAT|FALLO|RECURSO/.test(e);
}

// ¿El expediente está en fase de segunda instancia? (SEGUNDA_INSTANCIA / RECURSO_INTERPUESTO).
function enSegundaInstancia(estado?: string): boolean {
  const e = (estado || '').toUpperCase();
  return e.includes('SEGUNDA_INSTANCIA') || e.includes('RECURSO_INTERPUESTO');
}

// ¿El expediente está en fase de ejecución de sanciones / archivo definitivo?
function enEjecucion(estado?: string): boolean {
  const e = (estado || '').toUpperCase();
  return [
    'FALLO_EJECUTORIADO', 'EJECUCION_SANCION', 'SANCION_CUMPLIDA',
    'ARCHIVO_DEFINITIVO_HISTORICO',
  ].some((k) => e.includes(k)) || /EJECUC|SANCION|ARCHIVO_DEFINITIVO/.test(e);
}

// ---- Utilidades -----------------------------------------------------------

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  // Fechas de solo día (medianoche UTC): se leen tal cual para que no se corran un día.
  if (/^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/.test(iso)) {
    return `${+iso.slice(8, 10)}/${+iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  }
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}
// Fecha + HORA del comité (ORDEN 7). Se lee directo del ISO (sin conversión de
// huso) para que coincida EXACTO con lo digitado y con el calendario; la hora se
// omite cuando es 00:00 (comités legados guardados solo con fecha).
function fmtFechaHoraComite(iso?: string | null): string {
  if (!iso) return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  if (!a || !m || !d) return fmtFecha(iso);
  const dmy = `${d}/${m}/${a}`;
  const hhmm = iso.length >= 16 ? iso.slice(11, 16) : '';
  return hhmm && hhmm !== '00:00' ? `${dmy}, ${hhmm}` : dmy;
}
const hoy = () => new Date().toISOString().slice(0, 10);

// Catálogos mínimos (fallbacks; el backend valida a fondo).
// Mismos valores que los enums del backend (TipoActuacion / EtapaProcesal).
const TIPO_ACTUACION_LABEL: Record<string, string> = {
  AUTO_INHIBITORIO: 'Auto inhibitorio',
  AUTO_APERTURA_INDAGACION: 'Auto de apertura de indagación previa',
  AUTO_ARCHIVO_INDAGACION: 'Auto de archivo de la indagación',
  AUTO_APERTURA_INVESTIGACION: 'Auto de apertura de investigación',
  AUTO_DECRETA_PRUEBAS: 'Auto que decreta pruebas',
  AUTO_CIERRE: 'Auto de cierre de investigación',
  AUTO_TRASLADO_ALEGATOS: 'Auto de traslado para alegatos',
  ARCHIVO: 'Archivo',
  PLIEGO_CARGOS: 'Pliego de cargos',
  OFICIO_REMISORIO: 'Oficio remisorio',
  AUTO_DEFINE_TRAMITE: 'Auto que define el trámite',
  AUTO_RESUELVE_NULIDAD: 'Auto que resuelve nulidad',
  AUTO_VARIACION_CARGOS: 'Auto de variación de cargos',
  FALLO: 'Fallo',
  AUTO_CONCEDE_RECURSO: 'Auto que concede recurso',
  AUTO_RECHAZA_RECURSO: 'Auto que rechaza recurso',
  DECISION_SEGUNDA_INSTANCIA: 'Decisión de segunda instancia',
  ACTO_EJECUCION_SANCION: 'Acto de ejecución de la sanción',
  OFICIO: 'Oficio',
  OTRO: 'Otro',
};
const ETAPA_PROCESAL_LABEL: Record<string, string> = {
  RADICACION: 'Radicación',
  REPARTO: 'Reparto',
  ANALISIS_INICIAL: 'Análisis inicial',
  INDAGACION_PREVIA: 'Indagación previa',
  INVESTIGACION: 'Investigación',
  PRUEBAS: 'Pruebas',
  CIERRE: 'Cierre',
  ALEGATOS_PRECALIFICATORIOS: 'Alegatos precalificatorios',
  PLIEGO: 'Pliego de cargos',
  JUZGAMIENTO_ORDINARIO: 'Juzgamiento ordinario',
  JUZGAMIENTO_VERBAL: 'Juzgamiento verbal',
  VARIACION_CARGOS: 'Variación de cargos',
  ALEGATOS_CONCLUSION: 'Alegatos de conclusión',
  FALLO: 'Fallo',
  RECURSOS: 'Recursos',
  SEGUNDA_INSTANCIA: 'Segunda instancia',
  EJECUCION: 'Ejecución',
  ARCHIVO: 'Archivo',
};
// En la actuación del acta el tipo es OTRO: se nombra por su fundamento.
function tituloActuacion(a: Actuacion): string {
  if (a.tipo === 'OTRO' && a.fundamento_normativo) return a.fundamento_normativo;
  return TIPO_ACTUACION_LABEL[a.tipo] ?? a.tipo;
}
const MODALIDAD_CONDUCTA = ['INSTANTANEA', 'PERMANENTE', 'CONTINUADA', 'OMISIVA'];
const FORMA_CULPABILIDAD = ['DOLO', 'CULPA_GRAVISIMA', 'CULPA_GRAVE'];
const CALIFICACION = ['LEVE', 'GRAVE', 'GRAVISIMA'];

// ---- Pestaña del expediente -------------------------------------------------
// Botón de pestaña con ícono en cápsula y contador opcional; el estilo base
// (borde, verde activo, hover) lo pone el TabsList.
function Pestana({
  value, icon: Icon, n, className, children,
}: { value: string; icon: LucideIcon; n?: number; className?: string; children: React.ReactNode }) {
  return (
    <TabsTrigger value={value} className={cn('group', className)}>
      <span className="flex size-7 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-data-[state=active]:bg-primary-foreground/20 group-data-[state=active]:text-primary-foreground">
        <Icon className="size-4" />
      </span>
      {children}
      {!!n && (
        <span className="ml-0.5 min-w-5 rounded-full bg-primary/10 px-1.5 text-[11px] leading-5 text-primary group-data-[state=active]:bg-primary-foreground/25 group-data-[state=active]:text-primary-foreground">
          {n}
        </span>
      )}
    </TabsTrigger>
  );
}

// Código técnico (EN_ANALISIS_INICIAL) → texto legible (En análisis inicial).
function humanizar(code?: string | null): string {
  if (!code) return '—';
  const t = code.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// Dato (etiqueta + valor) en forma de píldora translúcida sobre el hero verde.
function ChipHero({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/25">
      <span className="text-white/70">{k}:</span>
      <span className="font-semibold text-white">{v}</span>
    </span>
  );
}

// ---- Navegación a la sección donde se registró un término ------------------
const RESALTE = ['ring-4', 'ring-amber-400', 'ring-offset-2', 'ring-offset-background'];

function destinoTermino(t: Termino, exp: ExpDetalle): DestinoSeccion {
  if (t.tipo_termino === 'PRESCRIPCION') {
    return { tab: 'hechos', ancla: 'hechos-expediente', label: 'Hechos (fecha de los hechos)' };
  }
  if (t.tipo_termino === 'PLAZO_REPARTO') {
    return exp.acta_reparto
      ? { tab: 'datos', ancla: 'acta-reparto', label: 'Acta de reparto' }
      : { tab: 'datos', ancla: 'datos-expediente', label: 'Datos del expediente' };
  }
  const e = t.etapa ?? '';
  if (e === 'PRUEBAS') return { tab: 'pruebas', label: 'Pruebas' };
  if (['ANALISIS_INICIAL', 'INDAGACION_PREVIA', 'INVESTIGACION', 'CIERRE', 'ALEGATOS_PRECALIFICATORIOS', 'PLIEGO'].includes(e)) {
    return { tab: 'instruccion', label: 'Instrucción' };
  }
  if (['JUZGAMIENTO_ORDINARIO', 'JUZGAMIENTO_VERBAL', 'VARIACION_CARGOS', 'ALEGATOS_CONCLUSION', 'FALLO', 'RECURSOS'].includes(e)
    && enJuzgamiento(exp.estado_actual)) {
    return { tab: 'juzgamiento', label: 'Juzgamiento' };
  }
  if (e === 'SEGUNDA_INSTANCIA' && enSegundaInstancia(exp.estado_actual)) return { tab: 'segunda-instancia', label: 'Segunda instancia' };
  if (e === 'EJECUCION' && enEjecucion(exp.estado_actual)) return { tab: 'ejecucion', label: 'Ejecución' };
  return { tab: 'actuaciones', label: 'Actuaciones' };
}

// ---- SemáforoBadge (reutilizable) -----------------------------------------

export function SemaforoBadge({ nivel }: { nivel: Semaforo }) {
  const map: Record<Semaforo, { txt: string; cls: string }> = {
    VERDE: { txt: 'En término', cls: 'bg-primary text-primary-foreground' },
    AMARILLO: { txt: 'Por vencer', cls: 'bg-amber-400 text-amber-950' },
    ROJO: { txt: 'Crítico', cls: 'bg-red-600 text-white' },
    NEGRO: { txt: 'Vencido', cls: 'bg-red-700 text-white' },
  };
  const it = map[nivel] ?? map.VERDE;
  return <Badge className={cn('border-transparent', it.cls)}>{it.txt}</Badge>;
}

function badgeFirma(e: Actuacion['estado_firma']) {
  switch (e) {
    case 'FIRMADO': return <Badge className="bg-primary text-primary-foreground border-transparent">Firmado</Badge>;
    case 'APROBADO': return <Badge className="bg-emerald-600 text-white border-transparent">Aprobado</Badge>;
    case 'EN_REVISION': return <Badge className="bg-amber-500 text-white border-transparent">En revisión</Badge>;
    default: return <Badge variant="secondary">Sin firmar</Badge>;
  }
}

// Sentinela para selects opcionales.
const NINGUNO = '__NINGUNO__';

// ===========================================================================
// Componente principal
// ===========================================================================

export default function ExpedienteDetalle({
  expedienteId,
  onVolver,
  onEditarEtapa2,
}: {
  expedienteId: string;
  onVolver: () => void;
  onEditarEtapa2?: (id: string) => void;
}) {
  const [exp, setExp] = useState<ExpDetalle | null>(null);
  const [actuaciones, setActuaciones] = useState<Actuacion[]>([]);
  const [terminos, setTerminos] = useState<Termino[]>([]);
  const [pruebas, setPruebas] = useState<Prueba[]>([]);
  const [nulidades, setNulidades] = useState<Nulidad[]>([]);
  // Auto de corrección de errores formales: pedido abierto y refresco del listado.
  const [corregir, setCorregir] = useState<PedidoCorreccion | null>(null);
  const [refrescarCorr, setRefrescarCorr] = useState(0);
  const [cargando, setCargando] = useState(true);
  // Pestaña activa (controlada) para poder saltar a una sección desde otra (p. ej. Términos).
  const [tab, setTab] = useState<string | null>(null);

  const usuario = usuarioActual();
  const rol = usuario?.rol;

  const cargar = useCallback(async () => {
    setCargando(true);
    // Lo secundario (actuaciones, términos, pruebas, nulidades) se carga APARTE y se
    // pinta cuando llega: si una consulta se demora (BD remota lenta), el expediente
    // igual se muestra apenas llegan sus datos, en vez de quedar en «Cargando…».
    const secundarios = Promise.all([
      getActuaciones(expedienteId).then(setActuaciones).catch(() => undefined),
      getTerminos(expedienteId).then(setTerminos).catch(() => undefined),
      getPruebas(expedienteId).then(setPruebas).catch(() => undefined),
      getNulidades(expedienteId).then(setNulidades).catch(() => undefined),
    ]);
    try {
      // Un reintento si falla (p. ej. un «pool timeout» puntual de la BD).
      const e = await getExpediente(expedienteId).catch(async () => {
        await new Promise((r) => setTimeout(r, 1500));
        return getExpediente(expedienteId);
      });
      setExp(e);
    } catch (err) {
      avisoError('No se pudo cargar el expediente', err instanceof Error ? err.message : undefined);
    } finally {
      setCargando(false);
    }
    await secundarios;
  }, [expedienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando && !exp) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={onVolver}><ArrowLeft className="size-4" /> Volver</Button>
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
      </div>
    );
  }

  if (!exp) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={onVolver}><ArrowLeft className="size-4" /> Volver</Button>
        <p className="py-8 text-center text-sm text-muted-foreground">Expediente no encontrado.</p>
      </div>
    );
  }

  // Lleva a la pestaña y sección donde se registró algo (flechita de Términos):
  // cambia de pestaña, desplaza hasta la sección y la resalta un momento.
  function irA(d: DestinoSeccion) {
    setTab(d.tab);
    window.setTimeout(() => {
      const el = document.getElementById(d.ancla ?? 'pestanas-expediente');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add(...RESALTE);
      window.setTimeout(() => el.classList.remove(...RESALTE), 2200);
    }, 120);
  }

  // Hay una decisión inicial en curso (no aprobada): se resalta la pestaña Instrucción.
  const decisionPendiente = !!exp.decision_inicial && exp.decision_inicial.estado !== 'APROBADA';
  // Ruta AUTO INHIBITORIO (la decisión inicial, o la que fijó comité/reparto): el
  // proceso se archiva, así que NO se muestran Términos, Pruebas, Nulidades,
  // Recursos ni Otros documentos. En las demás rutas sí.
  const esInhibitorio =
    (exp.decision_inicial?.tipo ?? caminoDeterminado(exp.acta_reparto)?.tipo) === 'AUTO_INHIBITORIO';
  const tabOculta = (t: string | null) =>
    esInhibitorio && !!t && ['terminos', 'pruebas', 'nulidades', 'recursos', 'otros-documentos'].includes(t);

  return (
    <div className="space-y-6">
      {/* Encabezado en el verde institucional (mismo hero que las demás vistas). */}
      <HeroVista
        icono={FolderOpen}
        titulo={<>Expediente {exp.radicado_manual || exp.radicado}</>}
        descripcion={
          <span className="mt-2 flex flex-wrap items-center gap-2">
            {exp.radicado_manual && exp.radicado_manual !== exp.radicado && (
              <ChipHero k="Radicado interno" v={exp.radicado} />
            )}
            <ChipHero k="Estado" v={humanizar(exp.estado_actual)} />
            {exp.etapa_actual && <ChipHero k="Etapa" v={ETAPA_PROCESAL_LABEL[exp.etapa_actual] ?? humanizar(exp.etapa_actual)} />}
            {exp.decision_inicial && <ChipHero k="Situación" v={labelSituacion(exp.decision_inicial.estado)} />}
          </span>
        }
        acciones={
          <Button onClick={onVolver} className={BOTON_CABECERA}>
            <ArrowLeft className="size-4" /> Volver
          </Button>
        }
      />

      <AvanceProceso etapaActual={exp.etapa_actual} estadoActual={exp.estado_actual} />

      {/* Alerta: fecha de los hechos tentativa (falta la real) */}
      {exp.hechos?.[0]?.fecha_hechos_tentativa && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            La <strong>fecha de los hechos es tentativa</strong>: este expediente está pendiente de
            registrar la fecha real. Diligénciela en la pestaña <strong>Hechos</strong> para recalcular la prescripción.
          </span>
        </div>
      )}

      {/* Si hay una decisión inicial pendiente, se abre directo en Instrucción. */}
      {/* Acumulación (art. 98): este expediente se acumuló a otro y su trámite sigue allá. */}
      {exp.acumulado_a && (
        <div className="flex items-start gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
          <FolderOpen className="mt-0.5 size-4 shrink-0 text-primary" />
          <span>
            Expediente <strong>acumulado al {exp.acumulado_a.radicado}</strong> (art. 98 Ley 1952 de 2019): quedó cerrado
            y su trámite continúa en el expediente principal.
          </span>
        </div>
      )}
      <ExpedienteActualContext.Provider value={exp.id}>
      <Tabs value={(tabOculta(tab) ? null : tab) ?? (decisionPendiente ? 'instruccion' : 'datos')} onValueChange={setTab}>
        <TabsList
          id="pestanas-expediente"
          className={cn(
            '!h-auto w-full flex-wrap items-center justify-start gap-2 rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/[0.06] via-background to-primary/[0.03] p-2.5 shadow-sm',
            // Cada pestaña luce como un botón real: tarjeta con ícono en cápsula, borde
            // suave, texto marcado y transición; crecen para llenar cada fila (sin hueco al final).
            "[&>[data-slot=tabs-trigger]]:h-auto [&>[data-slot=tabs-trigger]]:flex-auto [&>[data-slot=tabs-trigger]]:justify-center [&>[data-slot=tabs-trigger]]:gap-2 [&>[data-slot=tabs-trigger]]:rounded-xl [&>[data-slot=tabs-trigger]]:border [&>[data-slot=tabs-trigger]]:border-border/70 [&>[data-slot=tabs-trigger]]:bg-background [&>[data-slot=tabs-trigger]]:py-1.5 [&>[data-slot=tabs-trigger]]:pl-1.5 [&>[data-slot=tabs-trigger]]:pr-3.5 [&>[data-slot=tabs-trigger]]:font-semibold [&>[data-slot=tabs-trigger]]:text-foreground/80 [&>[data-slot=tabs-trigger]]:shadow-sm [&>[data-slot=tabs-trigger]]:transition-all [&>[data-slot=tabs-trigger]]:duration-200",
            // Hover: tinte verde institucional y leve elevación para invitar al clic.
            '[&>[data-state=inactive]]:hover:!border-primary/50 [&>[data-state=inactive]]:hover:!bg-primary/5 [&>[data-state=inactive]]:hover:!text-primary [&>[data-state=inactive]]:hover:-translate-y-0.5 [&>[data-state=inactive]]:hover:shadow-md',
            // La pestaña activa toma el verde institucional del programa (degradado)
            // con elevación (con ! para ganarle a la especificidad del estilo base).
            '[&>[data-state=active]]:!border-primary [&>[data-state=active]]:!bg-gradient-to-br [&>[data-state=active]]:!from-primary [&>[data-state=active]]:!to-primary/85 [&>[data-state=active]]:!text-primary-foreground [&>[data-state=active]]:shadow-lg [&>[data-state=active]]:shadow-primary/25 [&>[data-state=active]]:-translate-y-0.5',
          )}
        >
          <Pestana value="datos" icon={ClipboardList}>Datos</Pestana>
          <Pestana value="sujetos" icon={Users}>Sujetos</Pestana>
          <Pestana value="hechos" icon={FileSearch}>Hechos</Pestana>
          <Pestana value="actuaciones" icon={FileSignature} n={actuaciones.length}>Actuaciones</Pestana>
          {!esInhibitorio && <Pestana value="terminos" icon={Clock} n={terminos.length}>Términos</Pestana>}
          {/* Instrucción: se resalta en verde cuando hay una decisión inicial pendiente,
              para que el abogado/contratista/director vean claro dónde actuar. */}
          <Pestana
            value="instruccion"
            icon={Scale}
            className={cn(
              decisionPendiente &&
                'data-[state=inactive]:!border-primary/60 data-[state=inactive]:!bg-primary/10 data-[state=inactive]:!text-primary data-[state=inactive]:ring-2 data-[state=inactive]:ring-primary/20',
            )}
          >
            Instrucción
            {decisionPendiente && (
              <span className="relative ml-0.5 flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60 group-data-[state=active]:bg-primary-foreground" />
                <span className="relative inline-flex size-2 rounded-full bg-primary group-data-[state=active]:bg-primary-foreground" />
              </span>
            )}
          </Pestana>
          {enJuzgamiento(exp.estado_actual) && (
            <Pestana value="juzgamiento" icon={Gavel}>Juzgamiento</Pestana>
          )}
          {enSegundaInstancia(exp.estado_actual) && (
            <Pestana value="segunda-instancia" icon={Landmark}>Segunda instancia</Pestana>
          )}
          {enEjecucion(exp.estado_actual) && (
            <Pestana value="ejecucion" icon={Stamp}>Ejecución</Pestana>
          )}
          <Pestana value="documentos" icon={FolderOpen}>Documentos</Pestana>
          {!esInhibitorio && (
            <>
              <Pestana value="pruebas" icon={Microscope} n={pruebas.length}>Pruebas</Pestana>
              <Pestana value="nulidades" icon={ShieldAlert} n={nulidades.length}>Nulidades</Pestana>
              <Pestana value="recursos" icon={Scale}>Recursos</Pestana>
              <Pestana value="otros-documentos" icon={FilePlus2}>Otros documentos</Pestana>
            </>
          )}
          <Pestana value="suspension" icon={PauseCircle}>Suspensión</Pestana>
        </TabsList>

        {/* ---- Datos ---- */}
        <TabsContent value="datos">
          <div className="space-y-6">
          {/* De dónde vino el caso: el acta de reparto de la Etapa 1. */}
          {exp.acta_reparto && <BloqueActaReparto acta={exp.acta_reparto} />}
          <TarjetaSeccion id="datos-expediente" icon={FolderOpen} titulo="Datos del expediente" subtitulo="Información general y estado procesal.">
              <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Dato k="Radicado" v={exp.radicado} />
                <Dato k="Estado" v={<Badge>{exp.estado_actual}</Badge>} />
                <Dato k="Etapa" v={exp.etapa_actual || '—'} />
                <Dato k="Tipo de ingreso" v={exp.tipo_ingreso} />
                <Dato k="Modalidad de conducta" v={exp.modalidad_conducta || '—'} />
                <Dato k="Fecha de la notificación disciplinaria" v={fmtFecha(exp.fecha_queja)} />
                <Dato
                  k="Prescripción"
                  v={
                    <span className="flex items-center gap-2">
                      {fmtFecha(exp.fecha_prescripcion)}
                      {terminos.find((t) => /PRESCRIP/i.test(t.tipo_termino)) && (
                        <SemaforoBadge nivel={terminos.find((t) => /PRESCRIP/i.test(t.tipo_termino))!.semaforo} />
                      )}
                    </span>
                  }
                />
                <Dato k="Prioridad" v={exp.prioridad ? <Badge variant="secondary">{exp.prioridad}</Badge> : '—'} />
              </dl>
          </TarjetaSeccion>
          </div>
        </TabsContent>

        {/* ---- Sujetos ---- */}
        <TabsContent value="sujetos">
          <div className="space-y-6">
            <TarjetaSeccion icon={UserRound} titulo="Quejoso" subtitulo="Quien interpone la notificación disciplinaria.">
                {exp.quejosos && exp.quejosos.length > 0 ? (
                  <div className="space-y-4">
                    {exp.quejosos.map((q, i) => (
                      <div key={i} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <Dato k="Nombre" v={q.nombre || '—'} />
                        <Dato k="Documento" v={`${q.tipo_documento || ''} ${q.numero_documento || ''}`.trim() || '—'} />
                        <Dato k="Teléfono" v={q.telefono || '—'} />
                        <Dato k="Correo" v={q.correo || '—'} />
                        <Dato k="Municipio" v={q.municipio || '—'} />
                        <Dato k="Departamento" v={q.departamento || '—'} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Sin datos de quien interpone la notificación disciplinaria.</p>
                )}
            </TarjetaSeccion>

            <TarjetaSeccion
              icon={Users}
              titulo="Investigados"
              acciones={exp.investigados?.length ? <Badge className={INSIGNIA_CABECERA}>{exp.investigados.length}</Badge> : undefined}
            >
                {exp.investigados && exp.investigados.length > 0 ? (
                  <div className="space-y-4">
                    {exp.investigados.map((inv, i) => (
                      <div key={i} className="rounded-xl border border-primary/20 p-4">
                        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
                          <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">{i + 1}</span>
                          Investigado
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <Dato k="Nombre" v={inv.nombre || (inv.tipo === 'POR_ESTABLECER' ? 'Individualizar' : '—')} />
                          <Dato k="Documento" v={`${inv.tipo_documento || ''} ${inv.numero_documento || ''}`.trim() || '—'} />
                          <Dato k="Cargo" v={inv.cargo || '—'} />
                          <Dato k="Entidad" v={inv.entidad_nombre || '—'} />
                          <Dato k="Dependencia" v={inv.dependencia || '—'} />
                          <Dato k="Calidad" v={inv.calidad || '—'} />
                        </div>
                        <Apoderados inv={inv} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Sin investigados registrados.</p>
                )}
            </TarjetaSeccion>

            {exp.apoderados && exp.apoderados.length > 0 && (
              <TarjetaSeccion icon={Briefcase} titulo="Apoderado">
                  <div className="space-y-4">
                    {exp.apoderados.map((a, i) => (
                      <div key={i} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <Dato k="Nombre" v={a.nombre || '—'} />
                        <Dato k="Documento de identidad" v={a.numero_documento || '—'} />
                        <Dato k="Tarjeta profesional" v={a.tarjeta_profesional || '—'} />
                        <Dato k="Correo" v={a.correo || '—'} />
                        <Dato k="Dirección de notificación" v={a.direccion_notificacion || '—'} />
                      </div>
                    ))}
                  </div>
              </TarjetaSeccion>
            )}

            {/* Defensor actual y anteriores (designaciones y renuncias), según sus autos. */}
            <HistorialDefensores exp={exp} />
          </div>
        </TabsContent>

        {/* ---- Hechos ---- */}
        <TabsContent value="hechos">
          <HechosTab
            expedienteId={expedienteId}
            hecho={exp.hechos?.[0] ?? null}
            puedeEditar={PUEDE_EDITAR_HECHOS.includes(rol ?? '')}
            onActualizado={cargar}
          />
        </TabsContent>

        {/* ---- Actuaciones ---- */}
        <TabsContent value="actuaciones" className="space-y-6">
          <TarjetaSeccion
            id="historial-proceso"
            icon={History}
            titulo="Historial del proceso"
            subtitulo="Todo lo que ha pasado en el expediente, en orden. Use el ojito para ver los documentos."
            acciones={
              <Button
                size="sm"
                variant="outline"
                data-no-imprimir
                className={BOTON_CABECERA}
                title="Descarga el historial tal como se ve en pantalla (con el filtro y el orden elegidos)"
                onClick={() => {
                  const el = document.getElementById('historial-proceso');
                  if (!el) return;
                  const radicado = exp.radicado_manual || exp.radicado;
                  imprimirElementoComoPDF(el, {
                    titulo: `historial-${radicado}`,
                    encabezado: `Expediente ${radicado} · Generado el ${new Date().toLocaleString('es-CO')}`,
                  }).catch((err) => avisoError('No se pudo generar el PDF', err instanceof Error ? err.message : undefined));
                }}
              >
                <Printer className="size-3.5" /> Descargar PDF
              </Button>
            }
          >
            <LineaTiempoProceso
              exp={exp}
              actuaciones={actuaciones}
              pruebas={pruebas}
              nulidades={nulidades}
              terminos={terminos}
              tituloActuacion={tituloActuacion}
            />
          </TarjetaSeccion>
          <ActuacionesTab
            expedienteId={expedienteId}
            actuaciones={actuaciones}
            rol={rol}
            etapaActual={exp.etapa_actual}
            onCambio={cargar}
            onCorregir={(a) => setCorregir({ ref: a.id, acto: { titulo: tituloActuacion(a), fecha: a.fecha } })}
          />
          <PanelCorrecciones exp={exp} refrescar={refrescarCorr} onNueva={() => setCorregir({ ref: 'general' })} onCambio={cargar} />
        </TabsContent>

        {/* ---- Términos ---- */}
        <TabsContent value="terminos" className="space-y-6">
          <TarjetaSeccion
            icon={CalendarDays}
            titulo="Agenda y términos del expediente"
            subtitulo="Se arma sola con los términos, la prescripción, el comité, las pruebas y la agenda."
          >
            <AgendaExpediente exp={exp} terminos={terminos} pruebas={pruebas} />
          </TarjetaSeccion>
          <TablaTerminos
            terminos={terminos}
            destino={(t) => destinoTermino(t, exp)}
            onIr={irA}
          />
        </TabsContent>

        {/* ---- Instrucción ---- */}
        <TabsContent value="instruccion" className="space-y-6">
          {/* Flujo de decisión inicial (doble visto bueno): mientras esté en curso
              reemplaza las acciones sueltas; ya en firme queda como resumen. */}
          {exp.decision_inicial && (
            <PanelDecisionInicial exp={exp} onCambio={cargar} onEditarEtapa2={onEditarEtapa2} />
          )}
          {(!exp.decision_inicial ||
            (exp.decision_inicial.estado === 'APROBADA' &&
              exp.decision_inicial.tipo !== 'AUTO_INHIBITORIO')) && (
            <InstruccionTab exp={exp} onCambio={cargar} />
          )}
          {/* Corrección de errores formales general (también por actuación, en Actuaciones). */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-dashed border-primary/40 px-4 py-3">
            <span className="text-sm text-muted-foreground">
              ¿Hay un error de digitación, transcripción u omisión en un auto? Genere el <strong>auto de corrección de errores formales</strong> (art. 45 Ley 1437).
            </span>
            <Button size="sm" variant="outline" onClick={() => setCorregir({ ref: 'general' })}>
              <FileSignature className="size-3.5" /> Corregir error formal
            </Button>
          </div>
        </TabsContent>

        {/* ---- Juzgamiento ---- */}
        {enJuzgamiento(exp.estado_actual) && (
          <TabsContent value="juzgamiento">
            <PanelJuzgamiento exp={exp} onCambio={cargar} />
          </TabsContent>
        )}

        {/* ---- Segunda instancia ---- */}
        {enSegundaInstancia(exp.estado_actual) && (
          <TabsContent value="segunda-instancia">
            <PanelSegundaInstancia exp={exp} onCambio={cargar} />
          </TabsContent>
        )}

        {/* ---- Ejecución de sanciones / archivo definitivo ---- */}
        {enEjecucion(exp.estado_actual) && (
          <TabsContent value="ejecucion">
            <PanelSanciones exp={exp} onCambio={cargar} />
          </TabsContent>
        )}

        {/* ---- Documentos (gestor documental) ---- */}
        <TabsContent value="documentos">
          <PanelDocumentos expedienteId={expedienteId} exp={exp} actuaciones={actuaciones} pruebas={pruebas} nulidades={nulidades} onRefresh={cargar} />
        </TabsContent>

        {/* ---- Pruebas ---- */}
        <TabsContent value="pruebas">
          <PanelPruebas expedienteId={expedienteId} pruebas={pruebas} onCambio={cargar} />
        </TabsContent>

        {/* ---- Nulidades ---- */}
        <TabsContent value="nulidades">
          <PanelNulidades
            expedienteId={expedienteId}
            nulidades={nulidades}
            actuaciones={actuaciones}
            tituloActuacion={tituloActuacion}
            onCambio={cargar}
          />
        </TabsContent>

        {/* ---- Recursos (reposición / apelación / queja) ---- */}
        <TabsContent value="recursos">
          <PanelRecursos exp={exp} onCambio={cargar} />
        </TabsContent>

        {/* ---- Otros documentos (actas, oficios…) con flujo de firmas opcional ---- */}
        <TabsContent value="otros-documentos">
          <PanelOtrosDocumentos exp={exp} refrescar={refrescarCorr} onCorreccion={() => setCorregir({ ref: 'general' })} />
        </TabsContent>

        {/* ---- Suspensión de términos ---- */}
        <TabsContent value="suspension">
          <PanelSuspension expedienteId={expedienteId} terminos={terminos} onCambio={cargar} />
        </TabsContent>
      </Tabs>
      </ExpedienteActualContext.Provider>
      {corregir && (
        <DialogoCorreccion exp={exp} pedido={corregir}
          onClose={() => { setCorregir(null); setRefrescarCorr((n) => n + 1); cargar(); }} />
      )}
    </div>
  );
}

// Roles que pueden fijar la fecha real de los hechos (Instrucción).
const PUEDE_EDITAR_HECHOS = [
  'ADMINISTRADOR', 'DIRECTOR_INSTRUCCION', 'FUNCIONARIO_INSTRUCCION',
  'CONTRATISTA_INSTRUCCION', 'TECNOLOGO_APOYO', 'SECRETARIA',
];

// ---- Pestaña Hechos: datos + (si la fecha es tentativa) fijar la fecha real ----
function HechosTab({ expedienteId, hecho, puedeEditar, onActualizado }: {
  expedienteId: string;
  hecho: Hechos | null;
  puedeEditar: boolean;
  onActualizado: () => Promise<void> | void;
}) {
  const [fechaReal, setFechaReal] = useState('');
  const [guardando, setGuardando] = useState(false);
  const esTentativa = !!hecho?.fecha_hechos_tentativa;

  async function guardar() {
    if (!fechaReal) { avisoError('Seleccione la fecha real de los hechos.'); return; }
    setGuardando(true);
    try {
      await fijarFechaHechos(expedienteId, fechaReal);
      avisoExito('Fecha real registrada. Prescripción recalculada.');
      setFechaReal('');
      await onActualizado();
    } catch (e) {
      avisoError('No se pudo guardar la fecha real.', e instanceof Error ? e.message : undefined);
    } finally { setGuardando(false); }
  }

  if (!hecho) {
    return (
      <TarjetaSeccion icon={FileSearch} titulo="Hechos">
        <p className="text-sm text-muted-foreground">Sin hechos registrados.</p>
      </TarjetaSeccion>
    );
  }

  return (
    <TarjetaSeccion id="hechos-expediente" icon={FileSearch} titulo="Hechos" subtitulo="Circunstancias de tiempo, modo y lugar." contentClassName="space-y-4">
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Dato k="Fecha de los hechos" v={
            <span className="inline-flex items-center gap-2">
              {fmtFecha(hecho.fecha_hechos)}
              {esTentativa && <Badge variant="secondary"><AlertTriangle className="size-3" /> Tentativa</Badge>}
            </span>
          } />
          <Dato k="Lugar" v={hecho.lugar || '—'} />
          <Dato k="Dependencia involucrada" v={hecho.dependencia_involucrada || '—'} />
          <div className="sm:col-span-2 lg:col-span-3"><Dato k="Descripción" v={<span className="whitespace-pre-line font-normal">{hecho.descripcion || '—'}</span>} /></div>
          <div className="sm:col-span-2 lg:col-span-3"><Dato k="Observaciones" v={<span className="whitespace-pre-line font-normal">{hecho.observaciones || '—'}</span>} /></div>
        </dl>

        {esTentativa && puedeEditar && (
          <div className="space-y-3 rounded-md border border-amber-500/40 bg-amber-50/60 p-4 dark:bg-amber-950/20">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
              <AlertTriangle className="size-4" /> Registrar la fecha real de los hechos
            </div>
            <p className="text-xs text-muted-foreground">
              Al guardar se reemplaza la fecha tentativa y se recalcula la prescripción (Art. 33: fecha + 5 años).
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="fechaReal">Fecha real</Label>
                <Input id="fechaReal" type="date" value={fechaReal} onChange={(e) => setFechaReal(e.target.value)} />
              </div>
              <Button onClick={guardar} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar fecha real'}</Button>
            </div>
          </div>
        )}
        {esTentativa && !puedeEditar && (
          <p className="text-xs text-muted-foreground">La fecha es tentativa; un funcionario de Instrucción debe registrar la fecha real.</p>
        )}
    </TarjetaSeccion>
  );
}

// ---- Apoderados de un investigado (relación opcional del backend) ---------
function Apoderados({ inv }: { inv: unknown }) {
  const raw = inv as { apoderados?: { nombre?: string; numero_documento?: string }[] };
  const aps = Array.isArray(raw.apoderados) ? raw.apoderados : [];
  if (aps.length === 0) return null;
  return (
    <div className="mt-3 border-t pt-3">
      <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Apoderados</p>
      <ul className="list-inside list-disc text-sm">
        {aps.map((ap, j) => (
          <li key={j}>{ap.nombre || '—'} {ap.numero_documento ? `· ${ap.numero_documento}` : ''}</li>
        ))}
      </ul>
    </div>
  );
}

// ---- Bloque de dato (dt/dd) -----------------------------------------------
function Dato({ k, v }: { k: string; v: React.ReactNode }) {
  return <DatoTarjeta k={k} v={v} />;
}

// ===========================================================================
// Acta de reparto (Etapa 1) relacionada al expediente
// ===========================================================================

// Cómo se nombra cada soporte del comité en la lista de descargas.
const ETIQUETA_SOPORTE: Record<string, string> = {
  COMITE_ACTA: 'Acta del comité',
  DECISION_COMITE: 'PDF de la decisión',
  COMITE_CONSTANCIA: 'Constancia',
};

// Estado de la cadena del acta, en palabras.
const ACTA_ESTADO_LABEL: Record<string, string> = {
  PENDIENTE_SUSTANCIADOR: 'Pendiente del abogado sustanciador',
  PENDIENTE_DIRECTOR: 'Pendiente de firma del Director',
  DEVUELTA_TECNICO: 'Devuelta al técnico administrativo',
  FIRMADA: 'Firmada por el Director',
};

/**
 * El acta de reparto queda RELACIONADA al expediente: aquí se ve de dónde vino
 * el caso (radicado de origen, canal, fechas del reparto) y quiénes firmaron.
 * «Ver acta» abre el .docx oficial con el mismo previsualizador de la Etapa 1.
 */
function BloqueActaReparto({ acta }: { acta: ActaRepartoResumen }) {
  const [solicitud, setSolicitud] = useState<Solicitud | null>(null);
  const [firmantes, setFirmantes] = useState<Firmante[]>([]);
  const [abriendo, setAbriendo] = useState(false);

  // El acta se descarga solo cuando la piden (el detalle ya trae el resumen).
  async function verActa() {
    setAbriendo(true);
    try {
      const [s, f] = await Promise.all([
        obtenerSolicitud(acta.solicitud_id),
        listarFirmantes(),
      ]);
      setFirmantes(f);
      setSolicitud(s);
    } catch (e) {
      avisoError('No se pudo abrir el acta de reparto', e instanceof Error ? e.message : undefined);
    } finally {
      setAbriendo(false);
    }
  }

  const firmada = acta.acta_estado === 'FIRMADA';

  return (
    <TarjetaSeccion
      id="acta-reparto"
      icon={FileSignature}
      titulo="Acta de reparto"
      subtitulo="Etapa 1: cómo ingresó el expediente y a quién se le repartió."
      acciones={
        <>
          {acta.acta_estado && (
            <Badge className={INSIGNIA_CABECERA}>
              {firmada && <CheckCircle2 className="size-3.5" />}
              {ACTA_ESTADO_LABEL[acta.acta_estado] ?? acta.acta_estado}
            </Badge>
          )}
          <Button type="button" size="sm" className={BOTON_CABECERA} onClick={verActa} disabled={abriendo}>
            <Eye className="size-4" /> {abriendo ? 'Abriendo…' : 'Ver acta'}
          </Button>
        </>
      }
    >
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Dato k="Radicado de origen" v={acta.radicado_origen || '—'} />
          <Dato k="Canal de ingreso" v={acta.canal || '—'} />
          <Dato k="Fecha de ingreso" v={fmtFecha(acta.fecha_ingreso)} />
          <Dato k="Fecha de reparto" v={fmtFecha(acta.fecha_reparto)} />
          <Dato k="Fecha de asignación" v={fmtFecha(acta.fecha_asignacion)} />
          <Dato k="Plazo para resolver" v={fmtFecha(acta.plazo_abogado_fecha)} />
          <Dato k="Elaboró (técnico)" v={acta.tecnico_nombre || '—'} />
          <Dato
            k="Quien recibe (sustanciador)"
            v={acta.sustanciador_nombre || acta.abogado_nombre || '—'}
          />
          <Dato
            k="Firmó (Director)"
            v={
              acta.director_nombre
                ? `${acta.director_nombre}${acta.acta_firmada_en ? ` · ${fmtFecha(acta.acta_firmada_en)}` : ''}`
                : '—'
            }
          />
          <Dato k="Abogado asignado" v={acta.abogado_nombre || '—'} />
        </dl>

        {/* RESPUESTA DEL COMITÉ DE QUEJA: queda ligada al expediente para
            consultarla aquí (pedido del cliente ago-06). */}
        {acta.comite && (
          <div className="mt-6 overflow-hidden rounded-xl border border-primary/25">
            <p className="flex items-center gap-2 border-b border-primary/20 bg-primary/10 px-4 py-2.5 text-sm font-semibold uppercase tracking-wide text-primary">
              <Scale className="size-4" />
              Respuesta del comité jurídico
            </p>
            <div className="p-4">
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Dato
                k="Decisión"
                v={acta.comite.decision
                  ? <Badge variant="secondary">{labelDecision(acta.comite.decision)}</Badge>
                  : '—'}
              />
              <Dato k="Fecha y hora del comité" v={fmtFechaHoraComite(acta.comite.fecha_comite)} />
              <Dato k="Fecha de la decisión" v={fmtFecha(acta.comite.fecha_decision)} />
              {acta.comite.reprogramado && (
                <Dato k="Reprogramado para" v={fmtFechaHoraComite(acta.comite.fecha_comite_nueva)} />
              )}
              {/* Remisión por competencia: a quién se envió. */}
              {acta.comite.remision_destino && (
                <Dato k="Se remite a" v={acta.comite.remision_destino} />
              )}
            </dl>
            {acta.comite.otro_detalle && (
              <div className="mt-3">
                <Dato k="Detalle" v={acta.comite.otro_detalle} />
              </div>
            )}
            {acta.comite.justificacion && (
              <div className="mt-3">
                <Dato k="Justificación" v={acta.comite.justificacion} />
              </div>
            )}
            {/* Soportes cargados: acta del comité, PDF de la decisión, constancia. */}
            {acta.comite.soportes && acta.comite.soportes.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Soportes
                </p>
                <ul className="mt-2 space-y-1">
                  {acta.comite.soportes.map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => descargarDocumento(d.storage_key, d.nombre)}
                        className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-2 hover:underline"
                      >
                        <FileText className="size-3.5" />
                        {ETIQUETA_SOPORTE[d.categoria]}: {d.nombre}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            </div>
          </div>
        )}

      {solicitud && (
        <ActaRepartoDialog
          solicitud={solicitud}
          firmantes={firmantes}
          onClose={() => setSolicitud(null)}
        />
      )}
    </TarjetaSeccion>
  );
}

// ===========================================================================
// Tab: Actuaciones + cadena de firma
// ===========================================================================

function ActuacionesTab({
  expedienteId, actuaciones, rol, etapaActual, onCambio, onCorregir,
}: {
  expedienteId: string; actuaciones: Actuacion[]; rol?: string; etapaActual?: string | null; onCambio: () => void;
  // «Corregir error formal» de esa actuación (auto de corrección, art. 45 Ley 1437).
  onCorregir: (a: Actuacion) => void;
}) {
  const [nueva, setNueva] = useState(false);
  const [revisar, setRevisar] = useState<Actuacion | null>(null);
  const [viendo, setViendo] = useState<Actuacion | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  async function accion(id: string, fn: () => Promise<unknown>, ok: string) {
    setOcupado(id);
    try {
      await fn();
      avisoExito(ok);
      onCambio();
    } catch (err) {
      avisoError('No se pudo completar la acción', err instanceof Error ? err.message : undefined);
    } finally {
      setOcupado(null);
    }
  }

  return (
    <TarjetaSeccion
      icon={FileSignature}
      titulo="Actuaciones"
      subtitulo="Se registran automáticamente al avanzar el proceso."
      contentClassName="p-0"
      acciones={
        <Button size="sm" className={BOTON_CABECERA} onClick={() => setNueva(true)}>
          <FilePlus2 className="size-4" /> Nueva actuación
        </Button>
      }
    >
        <div className="flex items-start gap-2 border-b border-primary/15 bg-primary/5 px-5 py-3 text-sm text-foreground/80">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
          <span>
            El acta de reparto, la decisión inicial (auto inhibitorio, indagación o investigación),
            pruebas, nulidades, cierre, juzgamiento y demás autos <strong>se registran solos</strong>,
            y los que ya se firmaron en su flujo quedan <strong>firmados</strong>. Use
            «Nueva actuación» solo para documentos adicionales (oficios, constancias, etc.).
          </span>
        </div>
        <Table className="[&_td:first-child]:pl-5 [&_td:last-child]:pr-5">
          <TableHeader className="bg-primary/[0.06] [&_th]:font-semibold [&_th]:text-primary [&_th:first-child]:pl-5 [&_th:last-child]:pr-5">
            <TableRow>
              <TableHead>Actuación</TableHead>
              <TableHead>Etapa</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Firma</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {actuaciones.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="max-w-md whitespace-normal">
                  <p className="font-medium">{tituloActuacion(a)}</p>
                  {a.tipo !== 'OTRO' && a.fundamento_normativo && (
                    <p className="text-xs text-primary/80">{a.fundamento_normativo}</p>
                  )}
                  {a.descripcion && (
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground" title={a.descripcion}>{a.descripcion}</p>
                  )}
                  {a.documento_key && (
                    <button
                      type="button"
                      onClick={() => setViendo(a)}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
                    >
                      <Eye className="size-3.5" /> Ver documento
                    </button>
                  )}
                </TableCell>
                <TableCell>{ETAPA_PROCESAL_LABEL[a.etapa] ?? a.etapa}</TableCell>
                <TableCell>{fmtFecha(a.fecha)}</TableCell>
                <TableCell>{badgeFirma(a.estado_firma)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {a.estado_firma === 'SIN_FIRMAR' && (
                      <Button size="sm" variant="outline" disabled={ocupado === a.id}
                        onClick={() => accion(a.id, () => enviarRevision(a.id), 'Actuación enviada a revisión.')}>
                        <Send className="size-3.5" /> Enviar a revisión
                      </Button>
                    )}
                    {a.estado_firma === 'EN_REVISION' && puedeRevisar(rol) && (
                      <Button size="sm" variant="outline" disabled={ocupado === a.id}
                        onClick={() => setRevisar(a)}>
                        <ClipboardList className="size-3.5" /> Revisar
                      </Button>
                    )}
                    {a.estado_firma === 'APROBADO' && puedeFirmar(rol) && (
                      <Button size="sm" disabled={ocupado === a.id}
                        onClick={() => accion(a.id, () => firmarActuacion(a.id), 'Actuación firmada.')}>
                        <FileSignature className="size-3.5" /> Firmar
                      </Button>
                    )}
                    {a.estado_firma === 'FIRMADO' && (
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <CheckCircle2 className="size-3.5 text-primary" /> Firmada
                      </span>
                    )}
                    {/* Solo si hay algo que corregir: genera el auto de corrección de errores formales. */}
                    {!a.fundamento_normativo?.startsWith('Auto de corrección de errores formales') && (
                      <Button size="sm" variant="outline" onClick={() => onCorregir(a)}
                        title="Generar el auto de corrección de errores formales de esta actuación (art. 45 Ley 1437)">
                        <FileSignature className="size-3.5" /> Corregir error formal
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {actuaciones.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Aún no hay actuaciones. Se registrarán solas a medida que avance el proceso.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

      {nueva && (
        <ModalNuevaActuacion
          expedienteId={expedienteId}
          etapaActual={etapaActual}
          onClose={() => setNueva(false)}
          onListo={() => { setNueva(false); onCambio(); }}
        />
      )}
      {revisar && (
        <ModalRevisar
          actuacion={revisar}
          onClose={() => setRevisar(null)}
          onListo={() => { setRevisar(null); onCambio(); }}
        />
      )}
      {viendo?.documento_key && (
        <VisorDocumento path={viendo.documento_key} nombre={tituloActuacion(viendo)} onClose={() => setViendo(null)} />
      )}
    </TarjetaSeccion>
  );
}

function ModalNuevaActuacion({ expedienteId, etapaActual, onClose, onListo }: {
  expedienteId: string; etapaActual?: string | null; onClose: () => void; onListo: () => void;
}) {
  const [tipo, setTipo] = useState('OFICIO');
  const [etapa, setEtapa] = useState(etapaActual && ETAPA_PROCESAL_LABEL[etapaActual] ? etapaActual : 'ANALISIS_INICIAL');
  const [fundamento, setFundamento] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [documentoKey, setDocumentoKey] = useState('');
  const [reqNotif, setReqNotif] = useState(false);
  const [reqCom, setReqCom] = useState(false);
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await crearActuacion(expedienteId, {
        tipo, etapa,
        ...(fundamento.trim() ? { fundamentoNormativo: fundamento.trim() } : {}),
        ...(descripcion.trim() ? { descripcion: descripcion.trim() } : {}),
        ...(documentoKey.trim() ? { documentoKey: documentoKey.trim() } : {}),
        requiereNotificacion: reqNotif,
        requiereComunicacion: reqCom,
      });
      avisoExito('Actuación creada.');
      onListo();
    } catch (err) {
      avisoError('No se pudo crear la actuación', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePlus2 className="size-5 text-primary" /> Nueva actuación
          </DialogTitle>
          <DialogDescription>
            Para documentos adicionales: los autos del proceso ya se registran automáticamente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_ACTUACION_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Etapa</Label>
              <Select value={etapa} onValueChange={setEtapa}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ETAPA_PROCESAL_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fund">Fundamento normativo (opcional)</Label>
            <Input id="fund" value={fundamento} onChange={(e) => setFundamento(e.target.value)} placeholder="Ej: Art. 208 Ley 1952" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Descripción (opcional)</Label>
            <Textarea id="desc" rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </div>
          <CampoPDF
            label="Documento de soporte (opcional)"
            carpeta="ANEXOS"
            expedienteId={expedienteId}
            onChange={(path) => setDocumentoKey(path)}
          />
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={reqNotif} onChange={(e) => setReqNotif(e.target.checked)} className="size-4 accent-primary" />
              Requiere notificación
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={reqCom} onChange={(e) => setReqCom(e.target.checked)} className="size-4 accent-primary" />
              Requiere comunicación
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Crear'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ModalRevisar({ actuacion, onClose, onListo }: {
  actuacion: Actuacion; onClose: () => void; onListo: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function decidir(aprobado: boolean) {
    if (!aprobado && !motivo.trim()) { avisoError('Indique el motivo de la devolución.'); return; }
    setGuardando(true);
    try {
      await revisarActuacion(actuacion.id, { aprobado, ...(motivo.trim() ? { motivo: motivo.trim() } : {}) });
      avisoExito(aprobado ? 'Actuación aprobada.' : 'Actuación devuelta.');
      onListo();
    } catch (err) {
      avisoError('No se pudo registrar la revisión', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="size-5 text-primary" /> Revisar actuación
          </DialogTitle>
          <DialogDescription>{actuacion.tipo} · {actuacion.etapa}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="motivo">Motivo (obligatorio si se devuelve)</Label>
            <Textarea id="motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={guardando} onClick={() => decidir(false)}>Devolver</Button>
            <Button type="button" disabled={guardando} onClick={() => decidir(true)}>Aprobar</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Tab: Instrucción (acciones según estado)
// ===========================================================================

type CampoTipo = 'text' | 'date' | 'textarea' | 'bool' | 'select';
interface Campo {
  name: string;
  label: string;
  tipo: CampoTipo;
  required?: boolean;
  opciones?: string[];
  placeholder?: string;
  defecto?: string | boolean;
}

interface AccionDef {
  key: string;
  label: string;
  descripcion: string;
  icon: React.ReactNode;
  campos: Campo[];
  ejecutar: (exp: ExpDetalle, valores: Record<string, string | boolean>) => Promise<unknown>;
}

// Utilidad: extrae un id de una relación anidada del expediente (con distintos nombres).
function idDe(exp: ExpDetalle, ...claves: string[]): string {
  const raw = exp as unknown as Record<string, unknown>;
  for (const k of claves) {
    const v = raw[k];
    if (v && typeof v === 'object' && 'id' in (v as object)) return String((v as { id: unknown }).id);
    if (typeof v === 'string') return v;
  }
  return '';
}

// Todas las acciones disponibles, agrupadas y filtradas por estado.
const ACCIONES: AccionDef[] = [
  {
    key: 'auto-inhibitorio', label: 'Auto inhibitorio', icon: <ShieldAlert className="size-4" />,
    descripcion: 'Decisión de no dar trámite (no hay mérito para iniciar).',
    campos: [
      { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
      { name: 'motivo', label: 'Motivo', tipo: 'textarea', required: true },
      { name: 'documentoKey', label: 'Documento (key, opcional)', tipo: 'text' },
    ],
    ejecutar: (exp, v) => autoInhibitorio(exp.id, {
      fecha: v.fecha as string, motivo: v.motivo as string,
      ...(v.documentoKey ? { documentoKey: v.documentoKey as string } : {}),
    }),
  },
  {
    key: 'indagacion-previa', label: 'Abrir indagación previa', icon: <ClipboardList className="size-4" />,
    descripcion: 'Cuando falta identificar autor o determinar si hubo falta.',
    campos: [
      { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
      { name: 'objeto', label: 'Objeto de la indagación', tipo: 'textarea', required: true },
      { name: 'esDdhhDih', label: 'Asunto DDHH/DIH (+6 meses, Art. 208)', tipo: 'bool' },
      { name: 'documentoKey', label: 'Documento (key, opcional)', tipo: 'text' },
    ],
    ejecutar: (exp, v) => indagacionPrevia(exp.id, {
      fecha: v.fecha as string, objeto: v.objeto as string,
      esDdhhDih: !!v.esDdhhDih,
      ...(v.documentoKey ? { documentoKey: v.documentoKey as string } : {}),
    }),
  },
  {
    key: 'investigacion', label: 'Abrir investigación disciplinaria', icon: <FolderOpen className="size-4" />,
    descripcion: 'Apertura directa de investigación (Art. 209).',
    campos: [
      { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
      { name: 'documentoKey', label: 'Documento (key, opcional)', tipo: 'text' },
    ],
    ejecutar: (exp, v) => iniciarInvestigacion(exp.id, {
      fecha: v.fecha as string,
      ...(v.documentoKey ? { documentoKey: v.documentoKey as string } : {}),
    }),
  },
  {
    key: 'ind-autor', label: 'Registrar identificación de autor', icon: <ClipboardList className="size-4" />,
    descripcion: 'Marca si en la indagación se identificó al autor / DDHH.',
    campos: [
      { name: 'seIdentificoAutor', label: 'Se identificó al autor', tipo: 'bool' },
      { name: 'esDdhhDih', label: 'Asunto DDHH/DIH', tipo: 'bool' },
    ],
    ejecutar: (exp, v) => patchIndagacion(idDe(exp, 'indagacion_previa', 'indagacionPrevia', 'indagacion'), {
      seIdentificoAutor: !!v.seIdentificoAutor, esDdhhDih: !!v.esDdhhDih,
    }),
  },
  {
    key: 'ind-archivar', label: 'Archivar indagación', icon: <ShieldAlert className="size-4" />,
    descripcion: 'Archivo de la indagación previa (requiere auto).',
    campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
    ejecutar: (exp, v) => archivarIndagacion(idDe(exp, 'indagacion_previa', 'indagacionPrevia', 'indagacion'), { fecha: v.fecha as string }),
  },
  {
    key: 'ind-abrir-inv', label: 'Abrir investigación (desde indagación)', icon: <FolderOpen className="size-4" />,
    descripcion: 'Pasa de indagación previa a investigación disciplinaria.',
    campos: [],
    ejecutar: (exp) => abrirInvestigacionDesdeIndagacion(idDe(exp, 'indagacion_previa', 'indagacionPrevia', 'indagacion')),
  },
  {
    key: 'inv-prorroga', label: 'Prórroga de investigación', icon: <ClipboardList className="size-4" />,
    descripcion: 'Prórroga (Art. 213): solo con 2+ investigados o 2+ conductas.',
    campos: [
      { name: 'motivo', label: 'Motivo (opcional)', tipo: 'textarea' },
      { name: 'dosOMasConductas', label: '2 o más conductas', tipo: 'bool' },
    ],
    ejecutar: (exp, v) => prorrogaInvestigacion(idDe(exp, 'investigacion', 'investigacionDisciplinaria'), {
      ...(v.motivo ? { motivo: v.motivo as string } : {}),
      dosOMasConductas: !!v.dosOMasConductas,
    }),
  },
  {
    key: 'inv-version-libre', label: 'Versión libre', icon: <ClipboardList className="size-4" />,
    descripcion: 'Registra si el investigado rindió versión libre.',
    campos: [
      { name: 'seRindio', label: 'Se rindió versión libre', tipo: 'bool' },
      { name: 'documentoKey', label: 'Documento (key, opcional)', tipo: 'text' },
    ],
    ejecutar: (exp, v) => versionLibre(idDe(exp, 'investigacion', 'investigacionDisciplinaria'), {
      seRindio: !!v.seRindio,
      ...(v.documentoKey ? { documentoKey: v.documentoKey as string } : {}),
    }),
  },
  {
    key: 'inv-notificacion', label: 'Notificación de investigación', icon: <Send className="size-4" />,
    descripcion: 'Registra la notificación de la apertura de investigación.',
    campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
    ejecutar: (exp, v) => notificacionInvestigacion(idDe(exp, 'investigacion', 'investigacionDisciplinaria'), { fecha: v.fecha as string }),
  },
  {
    key: 'cerrar-investigacion', label: 'Cerrar investigación', icon: <ShieldAlert className="size-4" />,
    descripcion: 'Cierre de la investigación (no si hay pruebas pendientes).',
    campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
    ejecutar: (exp, v) => cerrarInvestigacion(exp.id, { fecha: v.fecha as string }),
  },
  {
    key: 'alegatos', label: 'Alegatos precalificatorios', icon: <ClipboardList className="size-4" />,
    descripcion: 'Registro de alegatos precalificatorios (Art. 220, 10 días).',
    campos: [
      { name: 'presento', label: 'Presentó alegatos', tipo: 'bool' },
      { name: 'fechaPresentacion', label: 'Fecha de presentación (opcional)', tipo: 'date' },
      { name: 'documentoKey', label: 'Documento (key, opcional)', tipo: 'text' },
    ],
    ejecutar: (exp, v) => alegatos(exp.id, {
      tipo: 'PRECALIFICATORIOS', presento: !!v.presento,
      ...(v.fechaPresentacion ? { fechaPresentacion: v.fechaPresentacion as string } : {}),
      ...(v.documentoKey ? { documentoKey: v.documentoKey as string } : {}),
    }),
  },
  {
    key: 'eval-archivo', label: 'Evaluación: archivo', icon: <ShieldAlert className="size-4" />,
    descripcion: 'Decisión de archivo en la evaluación (requiere auto).',
    campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
    ejecutar: (exp, v) => evaluacionArchivo(exp.id, { fecha: v.fecha as string }),
  },
  {
    key: 'pliego', label: 'Formular pliego de cargos', icon: <Stamp className="size-4" />,
    descripcion: 'Pliego de cargos con calificación provisional.',
    campos: [
      { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
      { name: 'calificacionProvisional', label: 'Calificación provisional', tipo: 'select', required: true, opciones: CALIFICACION, defecto: 'GRAVE' },
      { name: 'modalidadConducta', label: 'Modalidad de conducta', tipo: 'select', required: true, opciones: MODALIDAD_CONDUCTA, defecto: 'INSTANTANEA' },
      { name: 'formaCulpabilidad', label: 'Forma de culpabilidad', tipo: 'select', required: true, opciones: FORMA_CULPABILIDAD, defecto: 'DOLO' },
    ],
    ejecutar: (exp, v) => crearPliego(exp.id, {
      fecha: v.fecha as string,
      calificacionProvisional: v.calificacionProvisional as string,
      modalidadConducta: v.modalidadConducta as string,
      formaCulpabilidad: v.formaCulpabilidad as string,
    }),
  },
  {
    key: 'pliego-notificar', label: 'Notificar pliego', icon: <Send className="size-4" />,
    descripcion: 'Notificación del pliego de cargos (Art. 121/125, 15 días).',
    campos: [{ name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() }],
    ejecutar: (exp, v) => notificarPliego(idDe(exp, 'pliego', 'pliego_cargos', 'pliegoCargos'), { fecha: v.fecha as string }),
  },
  {
    key: 'remitir', label: 'Remitir a juzgamiento', icon: <Gavel className="size-4" />,
    descripcion: 'Remisión del expediente a juzgamiento (Art. 225, 3 días).',
    campos: [
      { name: 'fecha', label: 'Fecha', tipo: 'date', required: true, defecto: hoy() },
      { name: 'correoDestino', label: 'Correo destino (opcional)', tipo: 'text' },
    ],
    ejecutar: (exp, v) => remitirJuzgamiento(exp.id, {
      fecha: v.fecha as string,
      ...(v.correoDestino ? { correoDestino: v.correoDestino as string } : {}),
    }),
  },
];

// Qué acciones se muestran según el estado del expediente. Se usan coincidencias
// laxas por palabra clave para no acoplarse a la lista exacta de 23 estados.
function accionesParaEstado(estado: string): string[] {
  const e = (estado || '').toUpperCase();
  const set = new Set<string>();
  const has = (...k: string[]) => k.some((x) => e.includes(x));

  // Decisión inicial: expediente recién repartido / en trámite inicial.
  if (has('REPART', 'ASIGN', 'TRAMITE', 'RADICAD', 'INICIAL')) {
    set.add('auto-inhibitorio'); set.add('indagacion-previa'); set.add('investigacion');
  }
  // Indagación previa.
  if (has('INDAGAC')) {
    set.add('ind-autor'); set.add('ind-archivar'); set.add('ind-abrir-inv');
  }
  // Investigación.
  if (has('INVESTIGAC')) {
    set.add('inv-prorroga'); set.add('inv-version-libre'); set.add('inv-notificacion');
    set.add('cerrar-investigacion');
  }
  // Cierre / alegatos / evaluación.
  if (has('CIERRE', 'CERRAD', 'ALEGAT')) {
    set.add('alegatos'); set.add('eval-archivo'); set.add('pliego');
  }
  if (has('EVALUAC')) {
    set.add('eval-archivo'); set.add('pliego');
  }
  // Pliego.
  if (has('PLIEGO', 'CARGOS')) {
    set.add('pliego-notificar'); set.add('remitir');
  }
  if (has('REMISION', 'REMITIR')) set.add('remitir');

  // Si nada coincide, ofrecer al menos la decisión inicial (para no dejar vacío).
  if (set.size === 0) {
    set.add('auto-inhibitorio'); set.add('indagacion-previa'); set.add('investigacion');
  }
  return [...set];
}

function InstruccionTab({ exp, onCambio }: { exp: ExpDetalle; onCambio: () => void }) {
  const [accion, setAccion] = useState<AccionDef | null>(null);
  const disponibles = accionesParaEstado(exp.estado_actual);
  // Con la investigación abierta desde la decisión inicial, cierre y cargos están en los
  // «Siguientes pasos» de su procedimiento (no se repiten aquí).
  const enProcedimiento = exp.decision_inicial?.estado === 'APROBADA'
    && ['INVESTIGACION', 'INDAGACION_PREVIA'].includes(exp.decision_inicial?.tipo ?? '');
  // «Formular pliego de cargos» se hace con el AUTO DE FORMULACIÓN DE CARGOS (abajo):
  // al firmarlo el director se crea el pliego. Se sigue viendo ya en la etapa de pliego.
  const conAutoCargos = (disponibles.includes('pliego') || /PLIEGO|REMITIDO|JUZGAMIENTO|JUICIO/.test(exp.estado_actual ?? ''))
    && !enProcedimiento;
  // «Cerrar investigación» se hace con el AUTO DE CIERRE (art. 220): al firmarlo el director
  // se cierra la investigación y se corre el traslado de alegatos.
  const conAutoCierre = disponibles.includes('cerrar-investigacion') && !enProcedimiento;
  const acciones = ACCIONES.filter((a) => disponibles.includes(a.key) && a.key !== 'pliego' && a.key !== 'cerrar-investigacion');

  return (
    <TarjetaSeccion
      icon={Gavel}
      titulo="Acciones de instrucción"
      subtitulo="Acciones disponibles según el estado actual. Los bloqueos procesales del backend se muestran como error."
      acciones={<Badge className={INSIGNIA_CABECERA}>{exp.estado_actual}</Badge>}
      contentClassName="space-y-4"
    >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {acciones.map((a) => (
            <button
              key={a.key}
              onClick={() => setAccion(a)}
              className="flex flex-col items-start gap-1 rounded-xl border border-primary/20 border-l-[3px] border-l-primary bg-primary/[0.03] p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/10 hover:shadow-md"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-primary">{a.icon} {a.label}</span>
              <span className="text-xs text-muted-foreground">{a.descripcion}</span>
            </button>
          ))}
          {acciones.length === 0 && !conAutoCargos && !conAutoCierre && (
            <p className="text-sm text-muted-foreground">No hay acciones para este estado.</p>
          )}
        </div>

        {conAutoCierre ? (
          <AutoDecisionFlujo
            exp={exp} cfg={DOC_FLUJO_CIERRE} onCambio={onCambio}
            efecto="se cierra la investigación y se corre traslado de alegatos precalificatorios (10 días)"
            descripcion={<>Cuando se recaudaron las pruebas ordenadas (o venció el término), se redacta el auto sobre el
              formato oficial, prellenado con el expediente, se revisa y se envía: todos lo <strong>revisan</strong> y el
              <strong> único que firma es el director</strong>. Al firmarse se <strong>cierra la investigación</strong> y se
              corre el <strong>traslado de alegatos precalificatorios</strong>. Si hay pruebas pendientes y el término no
              ha vencido, no se podrá cerrar.</>}
          />
        ) : !enProcedimiento && (
          // Ya cerrada: el auto de cierre se sigue viendo (si se generó con el auto).
          <FormatoSiExiste exp={exp} cfg={DOC_FLUJO_CIERRE} efecto="se cerró la investigación" />
        )}

        {conAutoCargos && (
          <AutoDecisionFlujo
            exp={exp} cfg={DOC_FLUJO_FORMULACION} onCambio={onCambio}
            efecto="se formula el pliego de cargos y el expediente pasa a la etapa de pliego"
            descripcion={<>Evaluada la investigación (art. 221), se redacta sobre la minuta oficial, prellenado con el
              expediente y las <strong>pruebas practicadas</strong>. Complete los apartados (hechos, conducta, normas,
              ilicitud, culpabilidad, criterios del art. 47…), revise la previsualización y envíelo: todos lo
              <strong> revisan</strong> y el <strong>único que firma es el director</strong>. Al firmarse se formula el
              <strong> pliego de cargos</strong> con la calificación, modalidad y culpabilidad del auto.</>}
          />
        )}

      {accion && (
        <ModalAccion
          accion={accion}
          exp={exp}
          onClose={() => setAccion(null)}
          onListo={() => { setAccion(null); onCambio(); }}
        />
      )}
    </TarjetaSeccion>
  );
}

/** Muestra la tarjeta de un formato con flujo solo si ya se generó (consulta). */
function FormatoSiExiste({ exp, cfg, efecto }: { exp: ExpDetalle; cfg: DocFlujoConfig; efecto: string }) {
  const [existe, setExiste] = useState(false);
  useEffect(() => {
    let vivo = true;
    cfg.backend(exp.id).obtener().then((d) => { if (vivo) setExiste(!!d); }).catch(() => {});
    return () => { vivo = false; };
  }, [exp.id, cfg]);
  return existe ? <AutoDecisionFlujo exp={exp} cfg={cfg} efecto={efecto} /> : null;
}

function ModalAccion({ accion, exp, onClose, onListo }: {
  accion: AccionDef; exp: ExpDetalle; onClose: () => void; onListo: () => void;
}) {
  const [valores, setValores] = useState<Record<string, string | boolean>>(() => {
    const v: Record<string, string | boolean> = {};
    for (const c of accion.campos) {
      if (c.defecto !== undefined) v[c.name] = c.defecto;
      else if (c.tipo === 'bool') v[c.name] = false;
      else v[c.name] = '';
    }
    return v;
  });
  const [guardando, setGuardando] = useState(false);

  function set(name: string, val: string | boolean) {
    setValores((s) => ({ ...s, [name]: val }));
  }

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    for (const c of accion.campos) {
      if (c.required && (c.tipo === 'text' || c.tipo === 'textarea' || c.tipo === 'date' || c.tipo === 'select')) {
        if (!String(valores[c.name] ?? '').trim()) {
          avisoError(`El campo "${c.label}" es obligatorio.`);
          return;
        }
      }
    }
    setGuardando(true);
    try {
      await accion.ejecutar(exp, valores);
      avisoExito(`${accion.label}: acción registrada.`);
      onListo();
    } catch (err) {
      avisoError('No se pudo completar la acción', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{accion.icon} {accion.label}</DialogTitle>
          <DialogDescription>{accion.descripcion}</DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          {accion.campos.map((c) => (
            <div key={c.name} className="space-y-2">
              {c.tipo === 'bool' ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!valores[c.name]}
                    onChange={(e) => set(c.name, e.target.checked)}
                    className="size-4 accent-primary"
                  />
                  {c.label}
                </label>
              ) : c.tipo === 'select' ? (
                <>
                  <Label>{c.label}</Label>
                  <Select value={String(valores[c.name] ?? '')} onValueChange={(v) => set(c.name, v)}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
                    <SelectContent>
                      {(c.opciones ?? []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </>
              ) : c.tipo === 'textarea' ? (
                <>
                  <Label htmlFor={c.name}>{c.label}</Label>
                  <Textarea id={c.name} rows={3} value={String(valores[c.name] ?? '')} onChange={(e) => set(c.name, e.target.value)} />
                </>
              ) : (
                <>
                  <Label htmlFor={c.name}>{c.label}</Label>
                  <Input id={c.name} type={c.tipo === 'date' ? 'date' : 'text'} placeholder={c.placeholder}
                    value={String(valores[c.name] ?? '')} onChange={(e) => set(c.name, e.target.value)} />
                </>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Procesando…' : 'Confirmar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

